 "use client";

import * as React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { buildProductCarouselItems, ProductCardsCarousel } from "@/components/ui/product-cards-carousel";
import { SectionHeading } from "@/components/ui/section-heading";
import { useProductsStore } from "@/stores/product";
import ChevronLeft from "@/components/ui/chevron-left";
import ChevronRight from "@/components/ui/chevron-right";
import type { Swiper as SwiperClass } from "swiper/types";

export function ProductsSection() {
  const { products: strapiProducts, isLoading } = useProductsStore();
  const items = React.useMemo(() => {
    const builtItems = buildProductCarouselItems(strapiProducts);
    const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });

    return [...builtItems].sort(
      (a, b) => collator.compare(a.headerText, b.headerText) || collator.compare(a.title, b.title),
    );
  }, [strapiProducts]);
  const [swiper, setSwiper] = React.useState<SwiperClass | null>(null);
  const [isBeginning, setIsBeginning] = React.useState(true);
  const [isEnd, setIsEnd] = React.useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);

  const updateEdgeState = React.useCallback((instance: SwiperClass) => {
    setIsBeginning(instance.isBeginning);
    setIsEnd(instance.isEnd);
  }, []);

  React.useEffect(() => {
    if (!swiper) return;

    const handleTransitionStart = () => setIsTransitioning(true);
    const handleTransitionEnd = () => setIsTransitioning(false);

    swiper.on("transitionStart", handleTransitionStart);
    swiper.on("transitionEnd", handleTransitionEnd);

    return () => {
      swiper.off("transitionStart", handleTransitionStart);
      swiper.off("transitionEnd", handleTransitionEnd);
    };
  }, [swiper]);

  const handleManualNavigation = React.useCallback(
    (direction: "prev" | "next") => {
      if (!swiper) return;
      if (isTransitioning) return;
      if (direction === "prev") swiper.slidePrev();
      else swiper.slideNext();
    },
    [isTransitioning, swiper],
  );

  return (
    <section id="products" className="mx-auto w-full px-4 sm:px-6 lg:px-10">
      <SectionHeading
        eyebrow="Our Products"
        title="Catalyst For Success"
      />

      <div className="mx-auto mt-9 flex max-w-7xl items-center justify-between gap-2 overflow-visible sm:gap-4">
        <Button
          type="button"
          size="icon"
          className="z-10 hidden h-10 w-10 shrink-0 rounded-full bg-[#2F2F2F] text-white shadow-none hover:bg-[#1F1F1F] cursor-pointer disabled:cursor-not-allowed sm:inline-flex"
          onClick={() => handleManualNavigation("prev")}
          disabled={!swiper || isBeginning || isTransitioning}
          aria-label="Previous slide"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        <ProductCardsCarousel
          items={items}
          isLoading={isLoading}
          showNavigation={false}
          className="min-w-0 flex-1 overflow-visible"
          swiperClassName="overflow-visible pb-6"
          slideClassName="h-auto p-1"
          slidesPerView={1.1}
          spaceBetween={24}
          watchOverflow
          onSwiper={(instance) => {
            setSwiper(instance);
            updateEdgeState(instance);
          }}
          onSlideChange={(instance) => updateEdgeState(instance)}
          onResize={(instance) => updateEdgeState(instance)}
          breakpoints={{
            640: { slidesPerView: 1.4 },
            768: { slidesPerView: 3, spaceBetween: 24 },
          }}
        />

        <Button
          type="button"
          size="icon"
          className="z-10 hidden h-10 w-10 shrink-0 rounded-full bg-[#2F2F2F] text-white shadow-none hover:bg-[#1F1F1F] cursor-pointer disabled:cursor-not-allowed sm:inline-flex"
          onClick={() => handleManualNavigation("next")}
          disabled={!swiper || isEnd || isTransitioning}
          aria-label="Next slide"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="mt-10 flex justify-center">
        <Button
          asChild
          variant="default"
          className="rounded-full px-6 bg-primary text-[clamp(11px,0.95vw,13px)] font-semibold tracking-[0.18em]"
        >
          <Link href="/products">VIEW ALL</Link>
        </Button>
      </div>
    </section>
  );
}
